import React, { useState, useRef, useEffect } from "react";
import classes from "./OtpInput.module.scss";

interface OtpInputProps {
    value: string;
    onChange: (value: string) => void;
}

const OtpInput: React.FC<OtpInputProps> = ({ value, onChange }) => {
    const otpLength = 6;
    const [otpCode, setOtpCode] = useState<string[]>(new Array(otpLength).fill(""));
    const inputRefs = useRef<Array<HTMLInputElement | null>>([...Array(otpLength)]);

    // When the value prop changes, update the local state accordingly.
    useEffect(() => {
        const digits = value.split("");
        const padded = [...digits, ...new Array(otpLength - digits.length).fill("")];
        setOtpCode(padded.slice(0, otpLength));
    }, [value]);

    const handleOtpChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const newDigit = event.target.value;

        // Allow only a single digit (or empty string)
        if (!/^\d?$/.test(newDigit)) return;

        const newVals = [...otpCode];
        newVals[index] = newDigit;
        setOtpCode(newVals);

        // Call parent's onChange with the concatenated OTP code.
        onChange(newVals.join(""));

        if (newDigit && index < otpLength - 1) {
            // Move focus to the next input field if a digit is entered
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Backspace" && !otpCode[index] && index > 0) {
            const newVals = [...otpCode];
            newVals[index - 1] = "";
            setOtpCode(newVals);
            onChange(newVals.join(""));
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pastedText = event.clipboardData.getData("text").trim();

        // REGEX to check if the pasted text only contains digits
        if (pastedText.length === otpLength && /^\d+$/.test(pastedText)) {
            const newOtp = pastedText.split("");
            setOtpCode(newOtp);
            onChange(pastedText);

            // Move focus to the next input field if a digit is entered
            inputRefs.current[otpLength - 1]?.focus();
        }
    };

    return (
        <div className={classes.otpContainer}>
            {otpCode.map((digit, index) => (
                <input
                    key={index}
                    ref={(input) => (inputRefs.current[index] = input)}
                    type="text"
                    value={digit}
                    className={classes.otpInput}
                    title={`Digit ${index + 1}`}
                    onChange={(event) => handleOtpChange(index, event)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    onPaste={handlePaste}
                    inputMode="numeric"
                    maxLength={1}
                    required
                />
            ))}
        </div>
    );
};

export default OtpInput;
